import React, { useState, useEffect, useCallback } from 'react';
import ReactDOM from 'react-dom';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import AppActions from '../../actions/app-actions';
import {showError, uniqueKeyValue, getKeys, isViewerUser, setListItemStatus, setItemStatus} from '../../helpers';
import { useTableState, useTimeout } from '../../custom-hooks';
import Loader from '../common/loader';
import CTable from '../common/ctable';
import InfrastructureDialog from './infrastructure-dialog';
import InfrastructureExpanded from './infrastructure-expanded';
import { columns } from './infrastructure-table-def';
import InfrastructureFilter from './infrastructure-filter';
import { AppContext } from '../../contexts/app-context';

const timeoutInMilliSec = 30 * 1000;
const columnDefinition = columns();

const Infrastructure = (props) => {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uuidList, setUuidList] = useState([]);
  const [hasProxy, setHasProxy] = useState(false);
  const [proxyInfo, setProxyInfo] = useState({});
  const [dlg, setDlg] = useState({ open: false });
  const [regionList, setRegionList] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [startTimer] = useTimeout(refreshData, timeoutInMilliSec);
  const [filterselectedItem, setFilterSelectedItem] = useState('');
  const [filterSelectedType, setFilterSelectedType] = useState('All');
  const { colDef, selectedRows, onRowSelect, onAllRowSelect, onSelectedRowsReset, onFilterChange, onColumnChange } = useTableState(columnDefinition);

  function getVcByFilteredType () {
    if (filterSelectedType === 'All')
      return AppActions.getInfraStructuresAll();
    else if (filterSelectedType === 'By Device')
      return AppActions.getInfraByUuid(filterselectedItem);
    else
      return AppActions.getInfraByRegion(filterselectedItem);
  }

  const onAllSelected = useCallback(() => {
    setPageLoading(true);
    setFilterSelectedType('All');
    setFilterSelectedItem('');
    AppActions.getInfraStructuresAll()
      .then(res => {
        const vcList = res || [];
        setListItemStatus(vcList, 3);
        ReactDOM.unstable_batchedUpdates(() => {
          setList(vcList);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Cant' get the infrastructure`);
      });
  }, []);

  //VEP-2142
  /*const onRegionSelected = useCallback(selected => {
    setPageLoading(true);
    setFilterSelectedType('By Region');
    setFilterSelectedItem(selected);
    AppActions.getInfraByRegion(selected)
      .then(res => {
        const vcList = res.body || [];
        setListItemStatus(vcList, 3);
        ReactDOM.unstable_batchedUpdates(() => {
          setList(vcList);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Can't get the vcam`);
      });
  }, []);*/

  const onUuidSelected = useCallback(selected => {
    setPageLoading(true);
    setFilterSelectedType('By Device');
    setFilterSelectedItem(selected);
    AppActions.getInfraByUuid(selected)
      .then(res => {
        const vcList = res || [];
        setListItemStatus(vcList, 3);
        ReactDOM.unstable_batchedUpdates(() => {
          setList(vcList);
          setPageLoading(false);
        });
      })
      .catch(err => {
        setPageLoading(false);
        showError(err, `Can't get the infrastructure`);
      })
  }, []);

  const handleConfigureProxy = () => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (data) => {
      if(proxyInfo.id === undefined || proxyInfo.id === null || proxyInfo.id === '') {
        AppActions.updateFirstProxyInfo(data)
          .then(() => AppActions.setSnackbar(`proxy configuration updated successfully`))
          .catch(error => showError(error, `Failed to update proxy configuration`))
          .finally(() => setDlg(() => ({open: false})));
      } else {
        proxyInfo.url = data.url;
        proxyInfo.port = data.port;
        proxyInfo.user_name = data.user_name;
        proxyInfo.password = data.password;
        AppActions.updateSecondsProxyInfo(proxyInfo)
          .then(() => AppActions.setSnackbar(`proxy configuration updated successfully`))
          .catch(error => showError(error, `Failed to update proxy configuration`))
          .finally(() => setDlg(() => ({open: false})));
      }
    };
    setDlg({ open: true, proxyInfo, handleType: 'configureProxy', handleCancel, handleOk });
  }

  const handleCreateInfra = () => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = (data) => {
      AppActions.createInfrastructure(data)
        .then(res => {
          const { headers = {} } = res;
          const { location = '' } = headers;
          const aStr = location.split('/');
          return AppActions.getInfraStructureById(aStr[1]);
        })
        .then(res => {
          // To improve render performance. This may not required in future version of react
          ReactDOM.unstable_batchedUpdates(() => {
            const body = res.body;
            setItemStatus(body, 3);
            setList([...list, body]);
          });
          setDlg(() => ({ open: false }));
          AppActions.setSnackbar(`'${data.name}' infrastructure created successfully`);
        })
        .catch(err => showError(err, `Couldn't create infrastructure`));
    };
    setDlg({ open: true, handleType: 'create', handleCancel, handleOk });
  };

  const onDelete = useCallback(() => {
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      const delPromises = selectedRows.map(sr => AppActions.removeInfrastructure(sr.id));
      Promise.all(delPromises)
        .then(() => {
          const newList = list.filter(fId => (selectedRows.findIndex(srId => fId.id === srId.id)) === -1);
          ReactDOM.unstable_batchedUpdates(() => {
            setList(newList);
            onSelectedRowsReset();
          });
          AppActions.setSnackbar('Infrastructure deleted successfully');
        })
        .catch(err => showError(err, `Couldn't delete infrastructure`))
        .finally(() => setDlg(() => ({ open: false })));
    };
    setDlg({ open: true, handleType: 'remove', handleOk, handleCancel });
  }, [selectedRows, list]);

  const onSetDefault = useCallback(() => {
    const name = selectedRows[0].name;
    const id = selectedRows[0].id;
    const handleCancel = () => setDlg(() => ({ open: false }));
    const handleOk = () => {
      AppActions.setDefaultInfrastructure(id)
        .then(() => {
          const newList = list.map(item => {
            if (item.default) {
              return { ...item, default: false };
            } else if (id === item.id) {
              return { ...item, default: true };
            } else {
              return item;
            }
          });
          ReactDOM.unstable_batchedUpdates(() => {
            setList(newList);
            onSelectedRowsReset();
          });
          AppActions.setSnackbar(`successfully set '${name}' as default infrastructure`);
        })
        .catch(err => showError(err, `Couldn't set '${name} infrastructure as default`))
        .finally(() => setDlg(() => ({ open: false })));
    };
    setDlg({ open: true, name, handleType: 'setdefault', handleOk, handleCancel });
  }, [selectedRows, list]);

  const onUpdate = useCallback(data => {
    const newList = list.map(item => {
      if (item.id === data.id) {
        return {...item, ...data };
      } else {
        return item;
      }
    });
    setList(newList);
  }, [list]);

  const onExpand = useCallback(rowData => {
    return (
      <AppContext.Consumer>
        {({ updateRedirect }) => (
          <InfrastructureExpanded {...rowData} onUpdate={onUpdate}  redirect={props.redirect} updateRedirect={updateRedirect} proxyInfo={proxyInfo} />
        )}
      </AppContext.Consumer>
    )
  }, [onUpdate]);

  function table () {
    return (
      <div className='tab-container vcam-container'>
        <div className='leftFixed'>
          <InfrastructureFilter filterSelectedType={filterSelectedType} filterselectedItem={filterselectedItem} regionList={regionList} uuidList={uuidList} onAllSelected={onAllSelected}
            //VEP-2142
            //onRegionSelected={onRegionSelected}
            onUuidSelected={onUuidSelected}
          />
        </div>
        <div className='rightFluid vcam'>
          {
            pageLoading
              ? loader()
              : (
                <AppContext.Consumer>
                  {({ updateRedirect }) => (
                    <CTable
                      colDef={colDef}
                      data={list}
                      selectedRows={selectedRows}
                      noOfNonSelectableRows={1}
                      onRowSelect={!isViewerUser() ? onRowSelect : null}
                      onFilterChange={onFilterChange}
                      onColumnChange={onColumnChange}
                      onAllRowSelect={!isViewerUser() ? onAllRowSelect : null}
                      onDelete={onDelete}
                      onSetDefault={onSetDefault}
                      onExpand={onExpand}
                      redirect={props.redirect}
                      updateRedirect={updateRedirect}
                    />
                  )}
                </AppContext.Consumer>
              )
          }
        </div>
      </div>
    );
  }

  const loader = () => (<div><Loader show={true} /></div>);

  const body = () => {
    if (loading) {
      return loader();
    } else {
      return (
        <>
          <Grid container spacing={1}>
            <Grid item xs={12}>
              {buttons()}
            </Grid>
            <Grid item xs={12}>
              {table()}
            </Grid>
          </Grid>
          {dlg.open ? <InfrastructureDialog {...dlg} /> : null}
        </>
      );
    }
  };

  function buttons() {
    return (
      <Grid container justify='flex-end'>
        <Grid item>
          {
            hasProxy
              ? (
                <Button
                  color='gray'
                  variant='contained'
                  disabled={isViewerUser()}
                  style={{marginRight: 10}}
                  onClick={handleConfigureProxy}>
                  Configure Proxy
                </Button>
              )
              : null
          }
        </Grid>
        <Grid item>
          <Button
            color='primary'
            variant='contained'
            disabled={isViewerUser()}
            onClick={handleCreateInfra}>
            Create new Infrastructure
          </Button>
        </Grid>
      </Grid>
    )
  }

  function fetchDetails (didMount) {
    Promise.all([
      getVcByFilteredType(),
      AppActions.getInfraStructureAllWithSecificFields('nodes,region'),
      AppActions.getProxyInfo(),
      AppActions.getUserOrganization()
    ])
      .then(responses => {
        const addNodes = uniqueKeyValue();
        const addRegion = uniqueKeyValue();
        const iResponse = responses[1].body || [];
        setProxyInfo(responses[2].body || {});
        let uuids = {};
        let regions = {};
        iResponse.forEach(o => {
          const { nodes = [], region = '' } = o;
          regions = addRegion(region);
          nodes.forEach(n => uuids = addNodes(n));
        });
        // To improve render performance. This may not required in future version of react
        ReactDOM.unstable_batchedUpdates(() => {
          const vcList = responses[0] || [];
          setListItemStatus(vcList, 3);
          updateHasProxy(responses[3].tenant_features || []);
          setList(vcList);
          setUuidList(getKeys(uuids));
          setRegionList(getKeys(regions));
          if (didMount)
            setLoading(() => false);
        });
      })
      .catch(err => {
        showError(err);
        if (didMount)
          setLoading(() => false);
      })
      .finally(() => startTimer());
  }

  function updateHasProxy(tenantFeatures) {
    if(tenantFeatures !== undefined && tenantFeatures !== null && tenantFeatures.length > 0)
      setHasProxy(tenantFeatures[0].status);
  }

  function refreshData () {
    fetchDetails();
  }

  useEffect(() => fetchDetails(true), []);

  return (<>{body()}</>);
};

export default Infrastructure;
